import { useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText, ChevronDown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Generic export-as-CSV-or-XLSX dropdown.
 *
 * Props:
 *  - label: button label (default: "Esporta")
 *  - testid: data-testid for the trigger button
 *  - onExport: async (format: "csv"|"xlsx") => AxiosResponse with `responseType:'blob'`
 *  - filenameBase: e.g. "magazzino_prodotti" (used as fallback if no Content-Disposition)
 *  - extraOptions: optional array of [{label, days}] to render under "Periodo" (only for sales)
 *  - onExportPeriod: optional async (format, days) => AxiosResponse — used together with extraOptions
 */
export default function ExportMenu({
  label = "Esporta",
  testid = "export-btn",
  onExport,
  filenameBase = "export",
  extraOptions = null,
  onExportPeriod = null,
}) {
  const [busy, setBusy] = useState(false);

  const triggerDownload = async (format, days = null) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = days != null ? await onExportPeriod(format, days) : await onExport(format);
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Try Content-Disposition filename
      const cd = res.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const today = new Date().toISOString().slice(0, 10);
      const suffix = days ? `_${days}g` : "";
      a.download = match ? match[1] : `${filenameBase}${suffix}_${today}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export pronto", { description: a.download });
    } catch (err) {
      toast.error("Errore export", { description: err?.message || "Riprova" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid={testid}
          variant="outline"
          disabled={busy}
          className="border-[#EAE7E1] bg-white hover:bg-[#F4F1ED]"
        >
          {busy ? (
            <Loader2 size={16} className="mr-2 animate-spin" />
          ) : (
            <Download size={16} className="mr-2" />
          )}
          {label}
          <ChevronDown size={14} className="ml-1.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-white">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[#8E918C]">
          Formato
        </DropdownMenuLabel>
        <DropdownMenuItem
          data-testid={`${testid}-csv`}
          onClick={() => triggerDownload("csv")}
          className="cursor-pointer"
        >
          <FileText size={14} className="mr-2 text-[#5C5F5A]" />
          CSV (separato da ;)
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid={`${testid}-xlsx`}
          onClick={() => triggerDownload("xlsx")}
          className="cursor-pointer"
        >
          <FileSpreadsheet size={14} className="mr-2 text-[#2B5A3F]" />
          Excel (.xlsx)
        </DropdownMenuItem>

        {extraOptions && extraOptions.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[#8E918C]">
              Periodo (Excel)
            </DropdownMenuLabel>
            {extraOptions.map((opt) => (
              <DropdownMenuItem
                key={opt.days ?? "all"}
                data-testid={`${testid}-period-${opt.days ?? "all"}`}
                onClick={() => triggerDownload("xlsx", opt.days)}
                className="cursor-pointer"
              >
                <FileSpreadsheet size={14} className="mr-2 text-[#2B5A3F]" />
                {opt.label}
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
